import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Button, Divider, Modal, Space } from 'antd';

import {
  useDeleteTask,
  useTasksReduced,
} from '@/core/domain/operational/Tasks/Tasks.hooks';
import {
  TaskReduced,
  TasksQuery,
} from '@/core/domain/operational/Tasks/Tasks.types';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { PageProps } from '@/types/PageProps';

import AppPageHeader from '@/components/_common/AppPageHeader';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import TasksExtraFilters from '@/components/Operational/Tasks/TasksExtraFilters';
import TaskReportModal from '@/components/Operational/Tasks/TaskReportModal';
import { getTableScrollX } from '@/utils/table';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { useDebounce, useDisclosure } from '@/hooks';
import ChangePriceModal from '@/components/Operational/Tasks/ChangePriceModal';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';

function TasksPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();

  const [changePricesModal, setChangePriceModal] = useDisclosure(false);
  const [modalReportVisible, setReportModalVisible] = useState(false);

  const [queryParams, setQueryParams] = useState<TasksQuery>({
    order: 'ASC',
    active: true,
    ...convertParams<TasksQuery>({
      description: 'string',
      order: 'string',
      active: 'boolean',
      temparioCode: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams);

  const taskList = useTasksReduced(queryDebounce);
  const taskDelete = useDeleteTask();

  const tasksData = flatInfiniteData(taskList.data);

  usePersistSearchParams(queryDebounce);

  const handleDeleteTask = (task: TaskReduced) => {
    Modal.confirm({
      title: 'Confirmação de Remoção',
      content: (
        <p>
          Tem certeza de que deseja remover o seguinte serviço?
          <br />
          <br />"<strong>{task?.description}</strong>"
        </p>
      ),
      onOk: async () => {
        await taskDelete.mutateAsync(task.id);
      },
      okType: 'danger',
      okText: 'Remover',
      width: 500,
    });
  };

  function toggleReportModal() {
    setReportModalVisible(!modalReportVisible);
  }

  const columns: ColumnsType<TaskReduced> = [
    {
      key: 'temparioCode',
      dataIndex: 'temparioCode',
      title: intl.formatMessage({ id: 'tasks.general.form.temporaryCode' }),
      ellipsis: true,
      width: 150,
    },
    {
      key: 'description',
      dataIndex: 'description',
      title: intl.formatMessage({ id: 'tasks.general.form.description' }),
      ellipsis: true,
      width: 600,
    },
    {
      key: 'subAreaName',
      dataIndex: 'subAreaName',
      title: intl.formatMessage({ id: 'tasks.general.form.area' }),
      ellipsis: true,
      width: 100,
    },
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      fixed: 'right',
      align: 'right',
      width: 100,
      render: (row) => (
        <Space>
          <EditIcon
            redirect={`/app/operational/tasks/${row.id}/general`}
            disabled={!hasPermissions([Authorities.REGISTER_TASK_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleDeleteTask(row)}
            disabled={!hasPermissions([Authorities.REGISTER_TASK_DELETE])}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={() => toggleReportModal()}
              disabled={!hasPermissions([Authorities.REGISTER_TASK_REPORTS])}
            >
              {intl.formatMessage({
                id: 'tasks.page.button.generateReport',
              })}
            </Button>

            <Button
              block
              type="primary"
              onClick={setChangePriceModal.toggle}
              disabled={
                !hasPermissions([Authorities.REGISTER_TASK_CHANGE_PRICES])
              }
            >
              {intl.formatMessage({
                id: 'tasks.page.button.changePrices',
              })}
            </Button>

            <Button
              block
              type="primary"
              onClick={() => history.push('/app/operational/tasks/create')}
              disabled={!hasPermissions([Authorities.REGISTER_TASK_ADD])}
            >
              {intl.formatMessage({
                id: 'tasks.page.button.newTask',
              })}
            </Button>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          searchValue={queryParams?.search}
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          extraFilters={
            <TasksExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <InfiniteTable<TaskReduced>
          loading={taskList.isFetching}
          rowKey={(row) => row.id}
          dataSource={tasksData}
          columns={columns}
          hasNextPage={taskList.hasNextPage}
          fetchNextPage={taskList.fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <TaskReportModal
          visible={modalReportVisible}
          handleVisible={() => toggleReportModal()}
        />

        <ChangePriceModal
          visible={changePricesModal}
          handleVisible={setChangePriceModal.toggle}
        />
      </TabContentWrapper>
    </div>
  );
}

export default TasksPage;
